/**
 * DataTransformInspector — Phase 6a-3.
 *
 * Edits a `data_transform` node — the workflow's data-shaping primitive.
 * Two complementary modes (source-of-truth: soarNodes.js:3271):
 *
 *   1) Variable Assignments (path-based extraction)
 *      Each row binds a named variable to a template expression like
 *      `${upstream_node.result.field}`. Downstream nodes can reference
 *      the assigned name directly without re-traversing the upstream
 *      output object. This is the recommended path for most flows.
 *
 *   2) Transform Type + Expression (jq / future evaluators)
 *      Inline data-shaping language; today only `jq` is wired
 *      end-to-end (CanvasPageV3.jsx already disables inline-JS for RCE
 *      reasons). The dropdown surfaces the planned options so customers
 *      see the roadmap, but only jq is selectable as an active mode.
 *
 * The two modes are NOT mutually exclusive — the BE evaluates
 * assignments first, then runs the transform if set. Most workflows
 * use one or the other; this Inspector exposes both so authors can
 * combine them when needed.
 *
 * Spread-preservation contract per useCanvas:1098. Assignment edits
 * additionally splice on `assignments[index]` so per-row changes don't
 * blow away siblings.
 */

import React from 'react';
import { Plus, Trash2, ArrowUp, ArrowDown } from 'lucide-react';

// RC-DataTransform-Engines — all five engines are now wired through the
// BE (DAGNodeActivitiesImpl.executeDataTransform{,JsonPath,JmesPath,Regex,
// Template}). Each is sandbox-by-construction (no eval, no I/O, no host
// access): JQ via jackson-jq, JSONPath via Jayway, JMESPath via io.burt,
// Regex via Google RE2J (linear-time, no ReDoS), Template via Pebble
// (strict-variables, no file-system loader). All five share the compiled-
// query cache pattern.
const TRANSFORM_TYPES = [
  { value: '',         label: '(none — use Variable Assignments below)' },
  { value: 'jq',       label: 'JQ — safe pure-functional projection (recommended)' },
  { value: 'jsonpath', label: 'JSONPath — declarative JSON path queries' },
  { value: 'jmespath', label: 'JMESPath — JSON query with built-in functions' },
  { value: 'regex',    label: 'Regex — RE2 capture-group extraction' },
  { value: 'template', label: 'Template — Pebble template rendering' },
];

// All five transformType values are shipping with real BE evaluators
// (RC-DataTransform-Engines). Operator-facing label highlights JQ as
// the recommended default — JQ has the most expressive function
// library, the best documentation, and the longest in-tree tenure.
// The other four are equally first-class at runtime.
const DISABLED_TYPES = new Set(['jsonpath', 'jmespath', 'template', 'regex']);

export default function DataTransformInspector({ node, onUpdate, readOnly = false }) {
  const cfg = node.config || {};
  const assignments = Array.isArray(cfg.assignments) ? cfg.assignments : [];
  const outputVariable = cfg.outputVariable || '';
  const transformType = cfg.transformType || '';
  const expression = cfg.expression || '';

  const setField = (key, value) => {
    if (readOnly || typeof onUpdate !== 'function') return;
    onUpdate(node.id, { config: { ...cfg, [key]: value } });
  };

  // ── assignment helpers ──────────────────────────────────────────────
  const setAssignments = (next) => setField('assignments', next);

  const addAssignment = () => {
    if (readOnly) return;
    setAssignments([...assignments, { name: '', expression: '' }]);
  };
  const removeAssignment = (i) => {
    if (readOnly) return;
    setAssignments(assignments.filter((_, idx) => idx !== i));
  };
  const updateAssignment = (i, key, value) => {
    if (readOnly) return;
    const next = assignments.map((row, idx) =>
      idx === i ? { ...row, [key]: value } : row,
    );
    setAssignments(next);
  };
  const moveAssignment = (i, dir) => {
    if (readOnly) return;
    const j = i + dir;
    if (j < 0 || j >= assignments.length) return;
    const next = [...assignments];
    [next[i], next[j]] = [next[j], next[i]];
    setAssignments(next);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {/* ── Variable Assignments ───────────────────────────────────── */}
      <section style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          gap: 8,
        }}>
          <span style={{ fontSize: 11, fontWeight: 600, color: '#0f172a' }}>
            Variable Assignments
          </span>
          <button
            type="button"
            onClick={addAssignment}
            disabled={readOnly}
            style={addBtnStyle}
            title="Add a new variable assignment"
          >
            <Plus size={11} aria-hidden="true" /> Add
          </button>
        </div>
        <span style={{ fontSize: 10, color: '#64748b', lineHeight: 1.4 }}>
          Extract values from upstream node outputs using template syntax:{' '}
          <code style={codeChip}>{'${node_id.result.field}'}</code>
        </span>

        {assignments.length === 0 ? (
          <div style={emptyRow}>
            No assignments yet. Click <strong>Add</strong> to bind your first variable.
          </div>
        ) : (
          <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
            {assignments.map((row, i) => (
              <li
                key={i}
                style={{
                  display: 'flex', flexDirection: 'column', gap: 4,
                  padding: 8,
                  background: '#f8fafc',
                  border: '0.5px solid #e2e8f0',
                  borderRadius: 6,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <input
                    type="text"
                    value={row.name || ''}
                    onChange={(e) => updateAssignment(i, 'name', e.target.value)}
                    placeholder="variable_name"
                    disabled={readOnly}
                    autoComplete="off"
                    spellCheck={false}
                    style={{ ...inputStyle, flex: 1 }}
                  />
                  <RowActions
                    canUp={i > 0}
                    canDown={i < assignments.length - 1}
                    onUp={() => moveAssignment(i, -1)}
                    onDown={() => moveAssignment(i, 1)}
                    onRemove={() => removeAssignment(i)}
                    disabled={readOnly}
                  />
                </div>
                <input
                  type="text"
                  value={row.expression || ''}
                  onChange={(e) => updateAssignment(i, 'expression', e.target.value)}
                  placeholder="${upstream_node.result.field}"
                  disabled={readOnly}
                  autoComplete="off"
                  spellCheck={false}
                  style={{
                    ...inputStyle,
                    fontFamily: 'SFMono-Regular, Menlo, monospace',
                    fontSize: 11,
                  }}
                />
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* ── Output variable name ──────────────────────────────────── */}
      <FieldGroup
        label="Output Variable Name"
        hint="Name for this node's combined output (defaults to node ID)."
      >
        <input
          type="text"
          value={outputVariable}
          onChange={(e) => setField('outputVariable', e.target.value)}
          placeholder="extracted_data"
          disabled={readOnly}
          autoComplete="off"
          spellCheck={false}
          style={inputStyle}
        />
      </FieldGroup>

      {/* ── Transform type ────────────────────────────────────────── */}
      <FieldGroup
        label="Transform Type"
        hint='Pick "JQ" for inline shaping. Leave blank to use assignments above.'
      >
        <select
          value={transformType}
          onChange={(e) => setField('transformType', e.target.value)}
          disabled={readOnly}
          style={selectStyle}
        >
          {TRANSFORM_TYPES.map((t) => (
            <option
              key={t.value || '_none'}
              value={t.value}
              disabled={DISABLED_TYPES.has(t.value)}
            >
              {t.label}
            </option>
          ))}
        </select>
      </FieldGroup>

      {/* ── Expression ────────────────────────────────────────────── */}
      {transformType === 'jq' && (
        <FieldGroup
          label="JQ Expression"
          hint="Input is exposed as `.` — e.g. .foo.bar, .items | map(select(.active))"
        >
          <textarea
            value={expression}
            onChange={(e) => setField('expression', e.target.value)}
            placeholder='.policies | map({"const": .policy_number, "title": .policy_type})'
            disabled={readOnly}
            rows={5}
            spellCheck={false}
            style={{
              ...inputStyle,
              fontFamily: 'SFMono-Regular, Menlo, monospace',
              fontSize: 11,
              lineHeight: 1.4,
              resize: 'vertical',
            }}
          />
          <a
            href="https://jqlang.github.io/jq/manual/"
            target="_blank"
            rel="noopener noreferrer"
            style={{ fontSize: 10, color: '#0ea5e9', marginTop: 4, alignSelf: 'flex-start' }}
          >
            JQ language reference ↗
          </a>
        </FieldGroup>
      )}

      {transformType && transformType !== 'jq' && (
        <div style={{
          padding: 10,
          background: '#fffbeb',
          border: '0.5px solid #fcd34d',
          borderRadius: 6,
          fontSize: 11,
          color: '#78350f',
          lineHeight: 1.5,
        }}>
          <strong>{transformType}</strong> is not yet wired in the runtime — only{' '}
          <code style={codeChip}>jq</code> is evaluated today. Use Variable Assignments
          for now, or switch the type back to JQ.
        </div>
      )}
    </div>
  );
}

/* ───── per-row action cluster ─────────────────────────────────────── */
function RowActions({ canUp, canDown, onUp, onDown, onRemove, disabled }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
      <button type="button" onClick={onUp} disabled={disabled || !canUp}
        title="Move up" style={iconBtnStyle}>
        <ArrowUp size={11} aria-hidden="true" />
      </button>
      <button type="button" onClick={onDown} disabled={disabled || !canDown}
        title="Move down" style={iconBtnStyle}>
        <ArrowDown size={11} aria-hidden="true" />
      </button>
      <button type="button" onClick={onRemove} disabled={disabled}
        title="Remove this assignment" style={{ ...iconBtnStyle, color: '#b91c1c' }}>
        <Trash2 size={11} aria-hidden="true" />
      </button>
    </div>
  );
}

/* ───── shared field UI ───────────────────────────────────────────── */

function FieldGroup({ label, hint, children }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <span style={{ fontSize: 11, fontWeight: 600, color: '#0f172a' }}>{label}</span>
      {children}
      {hint && (
        <span style={{ fontSize: 10, color: '#64748b' }}>{hint}</span>
      )}
    </label>
  );
}

const inputStyle = {
  width: '100%',
  padding: '6px 8px',
  fontSize: 12,
  color: '#0f172a',
  background: '#ffffff',
  border: '0.5px solid #cbd5e1',
  borderRadius: 4,
  outline: 'none',
  fontFamily: 'inherit',
};

const selectStyle = { ...inputStyle, appearance: 'auto' };

const addBtnStyle = {
  display: 'inline-flex', alignItems: 'center', gap: 4,
  padding: '3px 8px',
  fontSize: 11, fontWeight: 500,
  color: '#0f172a',
  background: '#ffffff',
  border: '0.5px solid #cbd5e1',
  borderRadius: 4,
  cursor: 'pointer',
  fontFamily: 'inherit',
};

const iconBtnStyle = {
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  width: 22, height: 22, padding: 0,
  background: 'transparent', border: '0.5px solid transparent',
  borderRadius: 4, color: '#475569', cursor: 'pointer',
};

const emptyRow = {
  padding: '10px 12px',
  background: '#f8fafc',
  border: '0.5px dashed #cbd5e1',
  borderRadius: 6,
  fontSize: 11, color: '#64748b',
  textAlign: 'center',
  lineHeight: 1.5,
};

const codeChip = {
  padding: '0 4px',
  borderRadius: 3,
  background: '#f1f5f9',
  color: '#475569',
  fontFamily: 'SFMono-Regular, Menlo, monospace',
  fontSize: 10,
};
